// /home/vitruvio/puntum-frontend/src/components/Sidebar.jsx

import React, { useState, useEffect } from 'react'
import { useAuth } from '../context/AuthContext'

export default function Sidebar({ currentPage, onNavigate, onLogout }) {
  const [isOpen, setIsOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 768 : false)

  // 👤 Consumir el estado global del usuario desde AuthContext
  const { user } = useAuth()

  const userName = user?.nombre || user?.name || user?.empresa || 'Usuario'
  const userRole = user?.role || 'Administrador'
  const userPlan = user?.plan || 'Pro Trial'
  const trialDays = user?.trial_days_left !== undefined ? user.trial_days_left : 30
  const userAvatar = user?.avatar || user?.picture || null
  const needsCompanyUpdate = user?.needs_company_update ?? true

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768)
      if (window.innerWidth >= 768) setIsOpen(false)
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const menuItems = [
    { id: 'dashboard', icon: '📊', label: 'Dashboard' },
    { id: 'ventas', icon: '💰', label: 'Nueva Venta', isAction: true },
    { id: 'inventario', icon: '📦', label: 'Inventario' },
    { id: 'facturas', icon: '📥', label: 'Ingreso por Factura' },
    { id: 'historial', icon: '📋', label: 'Historial' },
    { id: 'contactos', icon: '👤', label: 'Contactos' },
    { id: 'movimientos', icon: '🔄', label: 'Movimientos' },
    { id: 'auditoria', icon: '🔍', label: 'Auditoría' },
    { id: 'reportes', icon: '📈', label: 'Reportes' },
    { id: 'sucursales', icon: '🏢', label: 'Sucursales' },
    { id: 'usuarios', icon: '👥', label: 'Usuarios' },
    { 
      id: 'configuracion', 
      icon: '⚙️', 
      label: 'Mi Empresa', 
      badge: needsCompanyUpdate ? '!' : null,
      badgeColor: '#f59e0b'
    },
    { id: 'suscripcion', icon: '💎', label: 'Mi Plan' }
  ]

  const handleNav = (id) => {
    onNavigate(id)
    if (isMobile) setIsOpen(false)
  }

  return (
    <>
      {/* Botón Hamburguesa para Móviles */}
      {isMobile && (
        <button 
          onClick={() => setIsOpen(true)}
          style={{
            position: 'fixed',
            top: '1rem',
            left: '1rem',
            zIndex: 40,
            background: '#ffffff',
            color: '#0f172a',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            padding: '0.6rem 0.8rem',
            cursor: 'pointer',
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          <span style={{ fontSize: '1.25rem' }}>☰</span> Menú
        </button>
      )}

      {/* Overlay Oscuro Móvil */}
      {isMobile && isOpen && (
        <div 
          onClick={() => setIsOpen(false)}
          style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.4)',
            backdropFilter: 'blur(2px)',
            zIndex: 45
          }}
        />
      )}

      {/* Menú Lateral */}
      <aside style={{
        width: '260px',
        minWidth: '260px',
        backgroundColor: '#ffffff',
        borderRight: '1px solid #e2e8f0',
        display: 'flex',
        flexDirection: 'column',
        position: isMobile ? 'fixed' : 'sticky',
        top: 0,
        height: '100vh',
        zIndex: 50,
        transform: isMobile ? (isOpen ? 'translateX(0)' : 'translateX(-100%)') : 'none',
        transition: 'transform 0.3s ease-in-out',
        boxShadow: isMobile && isOpen ? '4px 0 25px rgba(0,0,0,0.1)' : 'none'
      }}>
        {/* Encabezado Marca */}
        <div style={{ padding: '1.5rem', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#0f172a', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            📦 Puntum POS
          </h2>
          {isMobile && (
            <button onClick={() => setIsOpen(false)} style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer', color: '#64748b' }}>
              ✕
            </button>
          )}
        </div>

        {/* Bloque del Perfil de Usuario (Dinámico) */}
        <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          {userAvatar ? (
            <img 
              src={userAvatar} 
              alt={userName} 
              style={{ width: '38px', height: '38px', borderRadius: '50%', objectFit: 'cover', border: '1px solid #cbd5e1' }} 
            />
          ) : (
            <div style={{
              width: '38px',
              height: '38px',
              borderRadius: '50%',
              backgroundColor: '#3b82f6',
              color: '#ffffff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 'bold',
              fontSize: '1rem'
            }}>
              {userName.charAt(0).toUpperCase()}
            </div>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ 
              fontWeight: '600', 
              color: '#0f172a', 
              fontSize: '0.875rem', 
              whiteSpace: 'nowrap', 
              overflow: 'hidden', 
              textOverflow: 'ellipsis' 
            }}>
              {userName}
            </div>
            <div style={{ color: '#64748b', fontSize: '0.75rem', marginBottom: '0.35rem' }}>
              {userRole}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
              <span style={{ 
                fontSize: '0.7rem', 
                background: '#d1fae5', 
                color: '#065f46', 
                padding: '0.15rem 0.5rem', 
                borderRadius: '999px', 
                fontWeight: '600',
                display: 'inline-block'
              }}>
                {userPlan}
              </span>
              <span style={{ fontSize: '0.7rem', color: '#2563eb', fontWeight: 'bold' }}>
                ({trialDays}d)
              </span>
            </div>
          </div>
        </div>

        {/* Ítems del Menú */}
        <nav style={{ flex: 1, padding: '1rem', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          {menuItems.map(item => (
            <button
              key={item.id}
              onClick={() => handleNav(item.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                padding: '0.75rem 1rem',
                borderRadius: '8px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: currentPage === item.id ? '600' : '500',
                color: currentPage === item.id ? (item.isAction ? '#854d0e' : '#1d4ed8') : '#475569',
                backgroundColor: currentPage === item.id 
                  ? (item.isAction ? '#fef08a' : '#eff6ff') 
                  : (item.isAction ? '#fef9c3' : 'transparent'),
                textAlign: 'left',
                transition: 'all 0.2s ease'
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <span style={{ fontSize: '1.1rem' }}>{item.icon}</span>
                {item.label}
              </span>
              {item.badge && (
                <span style={{ 
                  background: item.badgeColor || '#ef4444', 
                  color: '#fff', 
                  fontSize: '0.75rem', 
                  width: '18px',
                  height: '18px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold' 
                }}>
                  {item.badge}
                </span>
              )}
            </button>
          ))}
        </nav>

        {/* Botón de Salida */}
        <div style={{ padding: '1rem', borderTop: '1px solid #e2e8f0' }}>
          <button
            onClick={onLogout}
            style={{
              width: '100%', padding: '0.75rem', background: '#fff', color: '#ef4444', 
              border: '1px solid #fecaca', borderRadius: '8px', cursor: 'pointer',
              fontWeight: '600', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem'
            }}
          >
            🚪 Cerrar Sesión
          </button>
        </div>
      </aside>
    </>
  )
}